import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/shared/ui/Modal';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Button } from '@/shared/ui/Button';
import { useToast } from '@/shared/ui/Toast';
import { agencyApi, type PracticeDetail } from '../api/agencyApi';
import { agencyKeys } from '../queryKeys';
import {
  DEFAULT_VERTICAL,
  VERTICAL_OPTIONS,
  specialtyOptions,
  type VerticalId,
} from '@/shared/constants/verticals';
import { validatePhone, formatPhoneDisplay, PHONE_PLACEHOLDER } from '@/shared/utils/validation';
import styles from './CreatePracticeModal.module.css';

interface EditPracticeModalProps {
  practice: PracticeDetail;
  isOpen: boolean;
  onClose: () => void;
}

export function EditPracticeModal({ practice, isOpen, onClose }: EditPracticeModalProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    name: practice.name,
    vertical: (practice.vertical ?? DEFAULT_VERTICAL) as VerticalId,
    specialty: practice.specialty ?? '',
    phone: practice.phone ?? '',
    website: practice.website ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Practice phone = primary location phone (the backend writes both).
  const primaryLocation = (practice.locations ?? []).find((l) => l.isDefault);

  useEffect(() => {
    setForm({
      name: practice.name,
      vertical: (practice.vertical ?? DEFAULT_VERTICAL) as VerticalId,
      specialty: practice.specialty ?? '',
      phone: practice.phone ?? '',
      website: practice.website ?? '',
    });
    setErrors({});
  }, [practice]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'Practice name is required';
    const phoneErr = validatePhone(form.phone);
    if (phoneErr) newErrors.phone = phoneErr;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      agencyApi.updatePractice(practice.id, {
        name: form.name,
        vertical: form.vertical,
        specialty: form.specialty || undefined,
        phone: form.phone || undefined,
        website: form.website || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practices() });
      toast.success('Practice updated.');
      onClose();
    },
    onError: () => toast.error('Failed to update practice.'),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Practice">
      <div className={styles.form}>
        <div className={styles.section}>
          <Input
            label="Practice Name"
            value={form.name}
            onChange={(e) => {
              setForm((p) => ({ ...p, name: e.target.value }));
              setErrors((p) => ({ ...p, name: '' }));
            }}
            error={errors.name}
            fullWidth
          />
          <Select
            label="Vertical"
            value={form.vertical}
            // Specialty is cleared with the vertical — the lists do not overlap.
            onChange={(e) =>
              setForm((p) => ({ ...p, vertical: e.target.value as VerticalId, specialty: '' }))
            }
            options={VERTICAL_OPTIONS}
            hint="Sets competitor discovery, market benchmarks and generated copy. Agency-only."
            fullWidth
          />
          <Select
            label="Specialty"
            value={form.specialty}
            onChange={(e) => setForm((p) => ({ ...p, specialty: e.target.value }))}
            options={specialtyOptions(form.vertical)}
            fullWidth
          />
          <Input
            label="Phone"
            type="tel"
            value={form.phone}
            onChange={(e) => {
              setForm((p) => ({ ...p, phone: e.target.value }));
              setErrors((p) => ({ ...p, phone: '' }));
            }}
            onBlur={(e) => setForm((p) => ({ ...p, phone: formatPhoneDisplay(e.target.value) }))}
            placeholder={PHONE_PLACEHOLDER}
            error={errors.phone}
            hint={
              primaryLocation
                ? `Also updates the primary location's phone (${primaryLocation.name}).`
                : undefined
            }
            fullWidth
          />
          <Input
            label="Website"
            value={form.website}
            onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
            placeholder="https://example.com"
            fullWidth
          />
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (validate()) mutate();
            }}
            loading={isPending}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>
  );
}
